// Печатные листы по документам 1С: УПД (ПП РФ № 1137), договор-счёт, коммерческое предложение.
//
// ЧТО ЭТО И ЧЕМ НЕ ЯВЛЯЕТСЯ. Лист для человека: распечатать, подписать, отдать. Это НЕ
// документ системы. Ни в 1С, ни в Bitrix отсюда не уходит ни байта: у ядра к 1С одна дверь
// и та только на чтение (`odata_route_get` в core_api.py отвергает даже ключи method/body).
// Оригинал документа живёт в 1С, номер присвоила 1С, и на листе это написано словами.
//
// ГЛАВНОЕ ПРАВИЛО ЭТОГО ФАЙЛА — ТО ЖЕ, ЧТО В print73.ts: ЛИСТ НЕ ДОДУМЫВАЕТ.
// Нет данных под графу — прочерк и оговорка внизу листа, сколько граф остались без отметок.
// Правило не абстрактное: в живых печатных формах порталов ровно этим сломано три места,
// и все три мы НЕ повторяем.
//   1. Портал 73 печатает `checks[i] === 'no' ? 'No' : 'Yes'` (73-portal/public/app.js:4165) —
//      пункт, по которому отметки не ставили вовсе, выходит на бумагу как пройденная проверка.
//   2. Официальная форма УПД портала 55 подставляет `unit.code || "796"` и `unit.name || "шт"`
//      (peleng-portal/src/worker.js:3935): единица измерения, которой в документе нет,
//      печатается как «796 шт». Графы 2 и 2а бланка 1137 — это ОКЕИ, и выдуманный ОКЕИ
//      уезжает в налоговую.
//   3. Там же: `taxIncluded = full.СуммаВключаетНДС !== false` (worker.js:3898) — отсутствие
//      поля молча читается как «сумма включает НДС», и вся раскладка базы и налога едет.
//      И `updFio(env, full.Руководитель_Key, "Ильин А. П.")` (worker.js:3916) — фамилия
//      подставляется константой, когда подписанта в документе нет.
// Здесь всё перечисленное даёт прочерк и строку в оговорке. Прочерк читается как «мы этого
// не знаем», выдуманное значение — как «мы это проверили».
//
// ОТКУДА БЕРУТСЯ ДАННЫЕ И ПОЧЕМУ НЕ ИЗ ЗЕРКАЛА.
// В зеркале (`ut_sales`, 1283 строки на 28.07.2026) от реализации есть ТОЛЬКО ШАПКА: номер,
// дата, суммы, флаги проведения, GUID покупателя и организации. Табличных частей нет вовсе
// (schema_1c_docs.sql:33 говорит это прямым текстом), справочников номенклатуры,
// контрагентов, ОКЕИ, стран и ГТД в схеме нет ни одного. Поэтому строки и реквизиты
// покупателя берутся живым чтением через прокси ядра (`/api/1c/<база>/…`, api/odata.ts),
// а из зеркала приходит только то, с чего начинается сборка. Возраст ответа 1С печатается
// на листе: прокси отдаёт устаревший кеш, когда 1С молчит, и лист обязан это сказать.
//
// ПОЧЕМУ ОТДЕЛЬНОЕ ОКНО, А НЕ @media print. Ровно те же две причины, что в print73.ts: лист
// не наследует ни одного правила styles.css, и сборка листа остаётся чистой функцией
// «данные → строка», которую можно проверить целиком без браузера. Этим и заняты проверки
// в tests/print1c.test.ts.

import type { OdataMeta } from '../api/odata'
import {
  БЛАНКИ_1С_СНЯТЫ, ГРАФЫ_УПД, КОЛОНКИ_КП, КОЛОНКИ_ПРОСТЫЕ, КОЛОНКИ_СЧЁТА, КПП_РАСХОЖДЕНИЕ,
  МЕСЯЦЫ, ОФЕРТА_КП, ПОЛЯ_ПЕРЕДАЧИ_УПД, ПОЛЯ_ШАПКИ_УПД, ПОЧЕМУ_НЕТ_ПОДПИСАНТОВ, ПРОДАВЕЦ,
  СТАВКИ_НДС, СТАТУС_УПД, ПУНКТЫ_ДОГОВОРА_СЧЁТА, БЛОК_РАБОТ_ДОГОВОРА_СЧЁТА,
} from './blanks1c'
import type { ГрафаУПД, КлючГрафы, КолонкаПростая, ПолеБланка } from './blanks1c'
import { plural } from './format'
// Расчёт КП живёт в lib/prices.ts и проверен там же. Печать его НЕ повторяет: цену строки
// и итог берём теми же функциями, которыми экран считает плитки, иначе лист и экран
// однажды разойдутся в третьем знаке и никто не скажет, какое число верно.
import { итогиКП, копейки, почемуНеГодна, ценаСтроки } from './prices'
import type { СтрокаКП, УсловияКП, ШапкаКП } from './prices'
// esc и открытьЛист общие для всех печатных листов CRM: две расходящиеся реализации
// экранирования — это дыра, а не независимость. Файл print73.ts при этом не меняется.
import { esc, моментом, открытьЛист } from './print73'
import type { ИсходПечати } from './print73'

export { esc, открытьЛист }
export type { ИсходПечати }

// ─────────────────────────── что кладём на лист ───────────────────────────

/** Строка табличной части документа, уже разобранная из ответа 1С. */
export interface СтрокаДокумента1С {
  /** Код номенклатуры — графа А. */
  код?: string | null
  /** Наименование — графа 1а. Содержание строки, иначе название номенклатуры. */
  наименование?: string | null
  /** Код вида товара (ТН ВЭД) — графа 1б. */
  кодВида?: string | null
  /** ОКЕИ — графа 2. */
  едКод?: string | null
  /** Условное обозначение единицы — графа 2а. */
  едИмя?: string | null
  /** Количество — графа 3. */
  количество?: number | null
  /** `Сумма` строки как её отдаёт 1С: с налогом или без — по флагу документа. */
  сумма?: number | null
  /** `Цена` строки. На бланк НЕ идёт: графа 4 считается из стоимости без налога. */
  ценаСырая?: number | null
  /** `СуммаНДС` строки. */
  ндс?: number | null
  /** `СтавкаНДС` строкой, как в 1С: «НДС22», «БезНДС». */
  ставка?: string | null
  /** Цифровой код страны — графа 10. */
  странаКод?: string | null
  /** Краткое наименование страны — графа 10а. */
  страна?: string | null
  /** Номер ГТД — графа 11. */
  гтд?: string | null
  /** Товар или услуга: у услуг единицы измерения в 1С нет по устройству. */
  вид?: 'товар' | 'услуга' | null
}

/** Реквизиты контрагента, как их отдаёт `Catalog_Контрагенты`. */
export interface Контрагент1С {
  наименование?: string | null
  инн?: string | null
  кпп?: string | null
  адрес?: string | null
}

/** Документ 1С, собранный для печати. */
export interface Документ1С {
  /** Что печатаем. От этого зависят заголовок и набор блоков. */
  вид: 'реализация' | 'счёт'
  номер?: string | null
  /** ISO-дата документа. */
  дата?: string | null
  /** Проведён ли в 1С. `null` — не знаем. */
  проведён?: boolean | null
  помеченНаУдаление?: boolean | null
  /**
   * Включает ли `Сумма` строки налог. `null` — поля в ответе не было, и это НЕ «включает»:
   * от этого флага зависит вся раскладка, поэтому неизвестность остаётся неизвестностью.
   */
  суммаВключаетНДС?: boolean | null
  покупатель?: Контрагент1С | null
  /** Грузополучатель. Пусто — на бланке «он же» не печатаем, печатаем покупателя. */
  грузополучатель?: string | null
  /** Основание передачи — графа [8]: договор или текст из документа. */
  договор?: string | null
  /** Валюта документа. Пусто — прочерк, а не «рубль». */
  валюта?: string | null
  строки: СтрокаДокумента1С[]
  /** `СуммаДокумента` из шапки — для сверки с суммой строк. */
  суммаДокумента?: number | null
  /** Откуда приехал документ: печатается на листе. */
  источник: 'зеркало' | '1c:ut' | '1c:bp'
  /** Ref_Key. Печатается мелко внизу: по нему документ ищут в 1С. */
  ref?: string | null
}

/** Условия сборки листа. Всё, что могло бы прийти из окружения, передаётся параметром. */
export interface УсловияПечати1С {
  /** Момент печати. Параметром — чтобы проверки не зависели от часов машины. */
  сейчас?: Date
  /** Свежесть ответа прокси 1С. `null` — спросить не удалось. */
  свежесть?: OdataMeta | null
  /** Что ещё не поместилось на лист: печатается отдельным списком в служебной отметке. */
  чегоНет?: string[]
}

// ─────────────────────────── прочерк и числа ───────────────────────────

/**
 * Прочерк. Отдельным классом, а не голым тире: по нему проверки отличают «нет данных»
 * от тире внутри чужого текста (в наименованиях номенклатуры дефисов полно).
 */
export const ПРОЧЕРК = '<span class="nil">—</span>'

/** Есть ли значение. Ноль и `false` — ЕСТЬ: «0 %» на бланке значит ноль процентов. */
export const естьЗначение = (v: unknown): boolean => {
  if (v === null || v === undefined) return false
  if (typeof v === 'number') return Number.isFinite(v)
  if (typeof v === 'string') return v.trim() !== ''
  return true
}

/** Пусто → прочерк, иначе экранированное значение. */
export function зн(v: unknown): string {
  return естьЗначение(v) ? esc(v) : ПРОЧЕРК
}

/**
 * Деньги: «1 234 567,89». Форматируем сами, а не через toLocaleString, по двум причинам.
 * Первая: toLocaleString в разных сборках Node ставит то обычный пробел, то U+00A0, и
 * проверка содержимого листа начинает зависеть от версии рантайма. Вторая: он молча
 * печатает «NaN» — здесь NaN обязан стать прочерком, как и всё остальное незнание.
 */
export function деньги(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ПРОЧЕРК
  const знак = n < 0 ? '−' : ''
  const [ц, к] = Math.abs(n).toFixed(2).split('.')
  return знак + ц.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + к
}

/** Количество: три знака после запятой — так требует графа 3 бланка. */
export function количество(n: unknown): string {
  if (typeof n !== 'number' || !Number.isFinite(n)) return ПРОЧЕРК
  const [ц, к] = Math.abs(n).toFixed(3).split('.')
  return (n < 0 ? '−' : '') + ц.replace(/\B(?=(\d{3})+(?!\d))/g, ' ') + ',' + к
}

/** «12 июля 2026 г.» — графа (1) бланка. */
export function датаДлинная(iso?: string | null): string {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return ПРОЧЕРК
  const мес = МЕСЯЦЫ[Number(m[2]) - 1]
  if (!мес) return ПРОЧЕРК
  return `${Number(m[3])} ${мес} ${m[1]} г.`
}

/** «12.07.2026». */
export function датаКороткая(iso?: string | null): string {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  return m ? `${m[3]}.${m[2]}.${m[1]}` : ПРОЧЕРК
}

/** Разложенная на части дата для строки «« 12 » июля 2026 года» в графе [11]. */
export function частиДаты(iso?: string | null): { дд: string; мм: string; гг: string } | null {
  const m = String(iso ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!m) return null
  const мес = МЕСЯЦЫ[Number(m[2]) - 1]
  return мес ? { дд: m[3], мм: мес, гг: m[1] } : null
}

// ─────────────────────────── НДС ───────────────────────────

/**
 * Ставка НДС в проценты. Три исхода, и путать их нельзя:
 *   • число      — ставка известна;
 *   • `null`     — «без НДС». Это ЗНАЧЕНИЕ: законная отметка бланка, а не пустота;
 *   • `undefined`— ставки нет или она незнакомая. Это НЕЗНАНИЕ: прочерк и строка в оговорке.
 *
 * Почему не «незнакомая = ноль». Ровно так сломался `INV_VAT_PCT` порталов
 * (worker.js:3382): он знает четыре ставки и всё остальное отдаёт нулём. Когда в 2026 году
 * появился НДС 22 %, каждая такая строка печаталась бы с нулевым налогом.
 */
export function процентСтавки(s?: string | null): number | null | undefined {
  const k = String(s ?? '').trim()
  if (!k) return undefined
  return k in СТАВКИ_НДС ? СТАВКИ_НДС[k] : undefined
}

/** Подпись ставки на бланке: «22 %», «без НДС» или прочерк. Никогда «0 %» вместо незнания. */
export function подписьСтавки(s?: string | null): string {
  const п = процентСтавки(s)
  if (п === undefined) return ПРОЧЕРК
  if (п === null) return 'без НДС'
  return `${п} %`
}

/** Строка документа, разобранная на графы 4, 5, 8 и 9. `null` в поле — значения нет. */
export interface РазобраннаяСтрока {
  /** Графа 5: стоимость без налога. */
  база: number | null
  /** Графа 8: сумма налога. */
  ндс: number | null
  /** Графа 9: стоимость с налогом. */
  всего: number | null
  /** Графа 4: цена за единицу без налога. */
  цена: number | null
  /**
   * Сумма строки в том базисе, в котором её выставили: `Сумма` документа, а если её нет —
   * цена × количество. На бланк 1137 НЕ идёт (там базис задан постановлением), но на КП
   * это и есть колонка «Сумма». Отличается от `база` и `всего` тем, что известна даже
   * когда флаг «сумма включает НДС» не пришёл: сколько написано в строке, столько и есть.
   */
  линия: number | null
}

/**
 * Разложить строку на графы бланка.
 *
 * `включает` — флаг документа «сумма включает НДС». `null` означает, что поля в ответе 1С
 * не было, и тогда база и итог остаются НЕИЗВЕСТНЫ: из одной только `Сумма` без этого флага
 * не следует ни то, ни другое. Портал 55 в этом месте подставляет «включает» и печатает
 * уверенные числа (worker.js:3898) — мы печатаем прочерк.
 *
 * Порядок предпочтений: сначала верим числам самой 1С (`Сумма`, `СуммаНДС`), и только если
 * `Сумма` не пришла — считаем из цены и количества, как это делает упрощённый лист портала.
 */
export function разложитьСтроку(
  стр: СтрокаДокумента1С, включает: boolean | null | undefined,
): РазобраннаяСтрока {
  const пусто: РазобраннаяСтрока = {
    база: null, ндс: null, всего: null, цена: null, линия: null,
  }
  const проц = процентСтавки(стр.ставка)

  const чис = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null

  // Что считаем «суммой строки»: то, что дала 1С, иначе цена × количество.
  let линия = чис(стр.сумма)
  if (линия === null) {
    const ц = чис(стр.ценаСырая)
    const к = чис(стр.количество)
    if (ц !== null && к !== null) линия = ц * к
  }
  if (линия === null) return пусто

  // Налог: сначала из документа, иначе выводим из ставки. Незнакомая ставка — не ноль.
  let налог = чис(стр.ндс)
  if (налог === null) {
    if (проц === null) налог = 0
    else if (typeof проц === 'number') {
      if (включает === true) налог = линия - линия / (1 + проц / 100)
      else if (включает === false) налог = (линия * проц) / 100
    }
  }

  let база: number | null = null
  let всего: number | null = null
  if (включает === true) {
    всего = линия
    база = налог === null ? null : линия - налог
  } else if (включает === false) {
    база = линия
    всего = налог === null ? null : линия + налог
  }
  // включает == null — раскладку не строим: см. шапку функции.

  const кол = чис(стр.количество)
  const цена = база !== null && кол !== null && кол !== 0 ? база / кол : null
  return { база, ндс: налог, всего, цена, линия }
}

/** Округление до копеек. Деньги нельзя таскать с хвостом 0.30000000000000004. */
const окр2 = (n: number): number => Math.round(n * 100) / 100

/** Итоги листа. `неполные` — хотя бы одна строка не разложилась: сумма ниже правды. */
export interface Итоги {
  база: number | null
  ндс: number | null
  всего: number | null
  /**
   * Сумма строк в базисе документа (см. `РазобраннаяСтрока.линия`). Нужна коммерческому
   * предложению: там «Сумма» — это то, что выставлено, а не графа бланка.
   */
  линии: number | null
  неполные: boolean
}

/**
 * Сложить строки. Если хоть одна графа не посчиталась, итог помечается неполным, и лист
 * говорит это словами. Молча просуммировать то, что посчиталось, значит напечатать «Всего
 * к оплате» меньше настоящего — самая дорогая ошибка из возможных на этом листе.
 */
export function итоги(строки: СтрокаДокумента1С[], включает: boolean | null | undefined): Итоги {
  let база = 0
  let ндс = 0
  let всего = 0
  let линии = 0
  let неполные = false
  let былиСтроки = false
  for (const с of строки) {
    const р = разложитьСтроку(с, включает)
    if (р.база === null || р.ндс === null || р.всего === null) неполные = true
    if (р.база !== null) база += р.база
    if (р.ндс !== null) ндс += р.ндс
    if (р.всего !== null) всего += р.всего
    if (р.линия !== null) линии += р.линия
    былиСтроки = true
  }
  if (!былиСтроки) {
    return { база: null, ндс: null, всего: null, линии: null, неполные: false }
  }
  return {
    база: окр2(база), ндс: окр2(ндс), всего: окр2(всего), линии: окр2(линии), неполные,
  }
}

// ─────────────────────────── сумма прописью ───────────────────────────

const ЕДИНИЦЫ = [
  '', 'один', 'два', 'три', 'четыре', 'пять', 'шесть', 'семь', 'восемь', 'девять', 'десять',
  'одиннадцать', 'двенадцать', 'тринадцать', 'четырнадцать', 'пятнадцать', 'шестнадцать',
  'семнадцать', 'восемнадцать', 'девятнадцать',
]
const ДЕСЯТКИ = [
  '', '', 'двадцать', 'тридцать', 'сорок', 'пятьдесят', 'шестьдесят', 'семьдесят',
  'восемьдесят', 'девяносто',
]
const СОТНИ = [
  '', 'сто', 'двести', 'триста', 'четыреста', 'пятьсот', 'шестьсот', 'семьсот',
  'восемьсот', 'девятьсот',
]

/** «21 рубль», «22 рубля», «25 рублей». */
function склонение(n: number, формы: [string, string, string]): string {
  const x = Math.abs(n) % 100
  const y = x % 10
  if (x > 10 && x < 20) return формы[2]
  if (y > 1 && y < 5) return формы[1]
  if (y === 1) return формы[0]
  return формы[2]
}

/** Тройка разрядов словами. `женский` — для тысяч: «одна тысяча», «две тысячи». */
function тройка(n: number, женский: boolean): string {
  const части: string[] = []
  const с = Math.floor(n / 100)
  const ост = n % 100
  const д = Math.floor(ост / 10)
  const е = n % 10
  if (с) части.push(СОТНИ[с])
  if (ост >= 10 && ост <= 19) {
    части.push(ЕДИНИЦЫ[ост])
  } else {
    if (д > 1) части.push(ДЕСЯТКИ[д])
    if (е) части.push(женский && е === 1 ? 'одна' : женский && е === 2 ? 'две' : ЕДИНИЦЫ[е])
  }
  return части.join(' ')
}

/**
 * «Сто двадцать три рубля 45 копеек». Перенос `rubToWords` из peleng-portal
 * (worker.js:10863) — тот же алгоритм, потому что формулировка уже стоит в подписанных
 * счетах и расходиться с ней нельзя.
 *
 * Возвращает `null`, когда суммы нет: пропись — единственное место листа, где выдуманный
 * ноль читается особенно убедительно («Ноль рублей 00 копеек» выглядит как решение, а не
 * как отсутствие данных). Вызывающий печатает прочерк.
 */
export function суммаПрописью(сумма: unknown): string | null {
  if (typeof сумма !== 'number' || !Number.isFinite(сумма)) return null
  const a = Math.round(сумма * 100) / 100
  const руб = Math.floor(Math.abs(a))
  const коп = Math.round((Math.abs(a) - руб) * 100)
  const млн = Math.floor(руб / 1_000_000)
  const тыс = Math.floor((руб % 1_000_000) / 1000)
  const ост = руб % 1000
  const части: string[] = []
  if (млн) части.push(тройка(млн, false) + ' ' + склонение(млн, ['миллион', 'миллиона', 'миллионов']))
  if (тыс) части.push(тройка(тыс, true) + ' ' + склонение(тыс, ['тысяча', 'тысячи', 'тысяч']))
  if (ост || (!млн && !тыс)) части.push(тройка(ост, false))
  const слова = части.join(' ').replace(/\s+/g, ' ').trim() || 'ноль'
  const s = (a < 0 ? 'минус ' : '') + слова
    + ' ' + склонение(руб, ['рубль', 'рубля', 'рублей'])
    + ' ' + String(коп).padStart(2, '0')
    + ' ' + склонение(коп, ['копейка', 'копейки', 'копеек'])
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// ─────────────────────────── чего на листе нет ───────────────────────────

/** Значение одной клетки таблицы и признак «это данные, а не прочерк». */
export interface Клетка {
  html: string
  /** false — под графой нет значения. Такая клетка идёт в оговорку. */
  есть: boolean
}

/**
 * Значение графы для строки документа.
 *
 * Две графы заполняет сам лист, и это не додумывание, а констатация:
 *   • графа 1 (№ п/п) — порядковый номер строки на бумаге;
 *   • графа 6 (сумма акциза) — «без акциза»: подакцизными товарами компания не торгует,
 *     и пустая клетка здесь читалась бы как «акциз забыли посчитать».
 * Всё остальное берётся из данных или становится прочерком.
 */
export function клеткаГрафы(
  г: ГрафаУПД, стр: СтрокаДокумента1С, разбор: РазобраннаяСтрока, номер: number,
): Клетка {
  const п: КлючГрафы | null = г.поле
  if (п === null) return { html: String(номер), есть: true }
  if (п === 'акциз') return { html: 'без акциза', есть: true }
  if (п === 'ставка') {
    const есть = процентСтавки(стр.ставка) !== undefined
    return { html: подписьСтавки(стр.ставка), есть }
  }
  if (п === 'ндс') {
    // «без НДС» — законная отметка графы 8, а не отсутствие числа.
    if (процентСтавки(стр.ставка) === null) return { html: 'без НДС', есть: true }
    return { html: деньги(разбор.ндс), есть: разбор.ндс !== null }
  }
  if (п === 'цена') return { html: деньги(разбор.цена), есть: разбор.цена !== null }
  // Колонки коммерческого предложения: цена и сумма в базисе самого предложения.
  // `ценаСырая` здесь — это цена, которую человек назвал клиенту, а не графа 4 бланка.
  if (п === 'ценаПредложения') {
    return { html: деньги(стр.ценаСырая), есть: естьЗначение(стр.ценаСырая) }
  }
  if (п === 'суммаПредложения') {
    return { html: деньги(разбор.линия), есть: разбор.линия !== null }
  }
  if (п === 'база') return { html: деньги(разбор.база), есть: разбор.база !== null }
  if (п === 'всего') return { html: деньги(разбор.всего), есть: разбор.всего !== null }
  if (п === 'количество') {
    return { html: количество(стр.количество), есть: естьЗначение(стр.количество) }
  }
  const v = стр[п as keyof СтрокаДокумента1С]
  return { html: зн(v), есть: естьЗначение(v) }
}

/** Сколько строк осталось без значения по каждой графе. */
export interface НедостачаГраф {
  /** Код графы → в скольких строках значения нет. Графы без пропусков сюда не попадают. */
  пропуски: Record<string, number>
  строк: number
  /** Сколько граф бланка заполнены хотя бы где-то. */
  заполнено: number
  всегоГраф: number
}

/** Пройти таблицу и посчитать, где прочерки. Считаем ДО вёрстки: числа идут в оговорку. */
export function недостачаГраф(
  строки: СтрокаДокумента1С[], включает: boolean | null | undefined, графы = ГРАФЫ_УПД,
): НедостачаГраф {
  const пропуски: Record<string, number> = {}
  for (const с of строки) {
    const р = разложитьСтроку(с, включает)
    for (const г of графы) {
      if (!клеткаГрафы(г, с, р, 1).есть) пропуски[г.код] = (пропуски[г.код] ?? 0) + 1
    }
  }
  const пустыхЦеликом = графы.filter((г) => (пропуски[г.код] ?? 0) === строки.length).length
  return {
    пропуски,
    строк: строки.length,
    заполнено: строки.length ? графы.length - пустыхЦеликом : 0,
    всегоГраф: графы.length,
  }
}

/**
 * Оговорка по таблице: «по N графам из M отметок нет».
 *
 * Разделяем два случая, потому что они значат разное. Графа, пустая ВО ВСЕХ строках, —
 * это «такого поля у нас нет вовсе» (страна происхождения, ГТД). Графа, пустая у ЧАСТИ
 * строк, — это «данные неполные», и человек должен видеть, у скольких именно.
 */
export function оговоркаПоГрафам(н: НедостачаГраф, графы = ГРАФЫ_УПД): string {
  if (!н.строк) return '<li>В документе нет ни одной строки — таблица пуста.</li>'
  const целиком: ГрафаУПД[] = []
  const частью: Array<{ г: ГрафаУПД; n: number }> = []
  for (const г of графы) {
    const n = н.пропуски[г.код] ?? 0
    if (n === 0) continue
    if (n === н.строк) целиком.push(г)
    else частью.push({ г, n })
  }
  const куски: string[] = []
  куски.push(`<li>Из ${н.всегоГраф} граф таблицы данные есть по ${н.заполнено}.</li>`)
  if (целиком.length) {
    const список = целиком
      .map((г) => `<b>${esc(г.код)}</b> — ${esc(г.из)}`)
      .join('; ')
    куски.push(`<li>По ${целиком.length} граф${склонение(целиком.length, ['е', 'ам', 'ам'])}`
      + ` отметок нет ни в одной из ${н.строк} строк, напечатан прочерк: ${список}.</li>`)
  }
  if (частью.length) {
    const список = частью
      .map(({ г, n }) => `<b>${esc(г.код)}</b> — нет у ${n} из ${н.строк}`)
      .join('; ')
    куски.push(`<li>Данные неполные: ${список}.</li>`)
  }
  if (!целиком.length && !частью.length) {
    куски.push('<li>Пропусков в таблице нет: все графы заполнены во всех строках.</li>')
  }
  return куски.join('')
}

/** Поля шапки и подвала, под которыми нет значения. Отдаёт список для оговорки. */
export function пустыеПоля(
  поля: ПолеБланка[], значения: Record<string, unknown>,
): ПолеБланка[] {
  return поля.filter((п) => !естьЗначение(значения[п.код]))
}

// ─────────────────────────── служебная отметка ───────────────────────────

/** Возраст ответа 1С словами: «14 с», «6 мин», «2 ч». Секунды нечитаемы после часа. */
export function возраст1С(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return 'неизвестен'
  const s = Math.max(0, Math.round(sec))
  if (s < 90) return `${s} с`
  const m = Math.round(s / 60)
  if (m < 90) return `${m} мин`
  return `${Math.round(m / 60)} ч`
}

/** Откуда приехал документ — словами, для служебной отметки. */
const ИСТОЧНИКИ: Record<Документ1С['источник'], string> = {
  'зеркало': 'зеркало Postgres (только шапка документа: строк табличной части в зеркале нет)',
  '1c:ut': 'живое чтение 1С, база УТ (склад)',
  '1c:bp': 'живое чтение 1С, база БП (бухгалтерия)',
}

/**
 * Служебная отметка CRM. Стоит ПЕРВОЙ на листе и подписана так, чтобы её нельзя было
 * принять за часть бланка. Ровно та же роль, что у `отметкаCRM` в print73.ts: лист,
 * который выглядит официально и несёт вчерашние цифры, хуже отсутствия листа.
 */
export function отметкаCRM1С(
  чтоЭто: string, чегоНет: string[], условия: УсловияПечати1С,
  источник?: Документ1С['источник'] | 'без-1с',
): string {
  const сейчас = условия.сейчас ?? new Date()
  const св = условия.свежесть ?? null
  // `без-1с` — лист, которому 1С не нужна вовсе (коммерческое предложение). Без этого
  // случая отметка печатала на КП «возраст данных 1С получить не удалось, считайте лист
  // справкой, а не выпиской» — предупреждение о данных, которых на листе нет ни одного,
  // и обещание «оригинал остаётся в 1С», хотя оригинала не существует. Ложная тревога
  // обесценивает настоящую: её перестают читать и на УПД.
  const без1С = источник === 'без-1с'
  const тревога = !без1С && !!св?.stale

  let строкаВозраста: string
  if (без1С) {
    строкаВозраста = 'Данные 1С этому листу не нужны: позиции, цены и условия введены'
      + ' человеком в CRM, в 1С за ними никто не ходил.'
  } else if (!св) {
    строкаВозраста = '<b>Возраст данных 1С получить не удалось</b> — прокси ядра не сообщил'
      + ' свежесть ответа. Считайте лист справкой, а не выпиской.'
  } else if (св.stale) {
    строкаВозраста = `<b>1С не ответила — данные из старого кеша, возраст ${esc(возраст1С(св.ageSec))}.</b>`
      + (св.error ? ` Ответ 1С: ${esc(св.error)}.` : '')
      + ' Перед подписью откройте документ в 1С и сверьтесь.'
  } else {
    const режим = св.cache === 'miss' ? 'прочитано из 1С только что' : 'из кеша ядра'
    строкаВозраста = `Данные 1С: ${esc(режим)}, возраст <b>${esc(возраст1С(св.ageSec))}</b>`
      + (св.ttlSec ? ` (ядро считает такой ответ свежим ${esc(возраст1С(св.ttlSec))})` : '') + '.'
  }

  const список = чегоНет.length
    ? `<div>Чего на этом листе нет:<ul>${чегоНет.map((с) => `<li>${с}</li>`).join('')}</ul></div>`
    : ''

  const чтоЗаДокумент = без1С
    ? '<b>Это не документ системы:</b> предложение нигде не сохраняется — ни в CRM, ни'
      + ' в 1С, ни в Bitrix, и номера ему система не присваивает.'
    : '<b>Это не документ системы:</b> в 1С и в Bitrix он не заливается, номер и оригинал'
      + ' остаются в 1С — у ядра к 1С только чтение.'

  return `<div class="crm-mark${тревога ? ' alarm' : ''}">
  <b>Служебная отметка CRM — не часть бланка.</b>
  ${esc(чтоЭто)} Лист собран в CRM ${esc(моментом(сейчас))}.
  ${чтоЗаДокумент}
  ${источник && !без1С ? `<div>Источник: ${esc(ИСТОЧНИКИ[источник])}.</div>` : ''}
  <div>${строкаВозраста}</div>
  ${список}
  <div>Прочерк «—» означает, что значения нет в источнике, а не что графа пустая в оригинале.
  Разметка бланка — копия печатных форм портала 55 от ${esc(БЛАНКИ_1С_СНЯТЫ)}.</div>
</div>`
}

// ─────────────────────────── каркас листа ───────────────────────────

/** Общие стили. Портрет — для счёта и КП; УПД добавляет свои поверх (лист альбомный). */
const СТИЛИ_ОБЩИЕ = `
*{box-sizing:border-box}
body{font:12px/1.42 Arial,Helvetica,sans-serif;color:#000;margin:0;padding:16px;background:#eef1f4}
.sheet{max-width:820px;margin:0 auto;background:#fff;padding:24px 30px;box-shadow:0 2px 12px rgba(0,0,0,.12)}
table{width:100%;border-collapse:collapse;margin:8px 0}
th,td{border:1px solid #999;padding:5px 7px;font-size:11px;vertical-align:top}
th{background:#f0f0f0;text-align:center;font-weight:700}
td.c{text-align:center}td.r{text-align:right}td.l{text-align:left}
.b{font-weight:700}
.nil{color:#888}
h1{font-size:14px;text-align:center;margin:12px 0 8px}
.pt{margin:4px 0;text-align:justify}
.sum{margin:6px 0}
.sign{margin-top:26px;display:flex;justify-content:space-between;gap:24px;font-size:12px}
.cap{font-size:9px;color:#555;text-align:center}
.crm-mark{border:1px solid #9aa4b2;background:#f4f6f9;padding:7px 10px;margin:0 0 12px;
  font-size:9.5px;line-height:1.45;color:#333;
  -webkit-print-color-adjust:exact;print-color-adjust:exact}
.crm-mark.alarm{border-color:#b42318;background:#fdf1f0;color:#7a1b13}
.crm-mark ul{margin:3px 0 0;padding-left:16px}
.noprint{max-width:820px;margin:0 auto 10px;display:flex;gap:8px;align-items:center;flex-wrap:wrap}
.noprint button{font-size:13px;padding:8px 14px;border-radius:8px;border:1px solid #0ea5e9;
  background:#0ea5e9;color:#fff;cursor:pointer}
@media print{
  body{background:#fff;padding:0}
  .sheet{box-shadow:none;max-width:none;padding:0}
  .noprint{display:none}
  table{page-break-inside:avoid}
}
`

/** Собрать самодостаточный документ. Кнопка печати — как в print73.ts, без авто-print. */
function страница(заголовок: string, тело: string, стили = ''): string {
  return '<!doctype html><html lang="ru"><head><meta charset="utf-8">'
    + '<meta name="viewport" content="width=device-width,initial-scale=1">'
    + `<title>${esc(заголовок)}</title><style>${СТИЛИ_ОБЩИЕ}${стили}</style></head><body>`
    + '<div class="noprint"><button type="button" onclick="window.print()">🖨 Печать / Сохранить в PDF</button></div>'
    + `<div class="sheet">${тело}</div></body></html>`
}

/** Отметка «проведён / черновик». Неизвестность отдельным словом, а не «черновик». */
function значокПроведения(проведён?: boolean | null): string {
  if (проведён === true) return '<span style="color:#0a7d33;font-weight:700">● Проведён в 1С</span>'
  if (проведён === false) return '<span style="color:#b8860b;font-weight:700">○ Черновик (не проведён)</span>'
  return '<span style="color:#666;font-weight:700">◌ Проведён или нет — неизвестно</span>'
}

// ═══════════════════════════ УПД: официальная форма (ПП 1137) ═══════════════════════════

const СТИЛИ_УПД = `
@page{size:A4 landscape;margin:8mm}
body{font-size:9px}
.sheet{max-width:1120px;padding:14px 16px}
.noprint{max-width:1120px}
.hdr td{border:0;vertical-align:top;padding:0 4px;font-size:8.3px;line-height:1.16}
.hdr table{margin:0}
.tt{table-layout:fixed;margin-top:0}
.tt td,.tt th{border:.9px solid #000;padding:3px;text-align:center;vertical-align:middle;
  font-size:8.8px;line-height:1.05;word-wrap:break-word;overflow-wrap:break-word}
.tt th{font-weight:400;background:#fff;padding:1.5px 3px}
.tt tr.code td{padding:1px 3px;font-size:8px}
.zdiv,.tt .c1{border-right:1.7px solid #000}
.lbl{font-size:8px}
.uline{border-bottom:1px solid #000}
.mk{font-size:8px;white-space:nowrap;width:26px}
h1{font-size:13px}
.upd-cap{font-size:7px;color:#333;text-align:center}
`

/** Клетка шапки: подпись, значение на линейке и номер графы в скобках. */
const строкаШапки = (подпись: string, значение: string, код: string): string =>
  `<tr><td class="lbl">${подпись}</td>`
  + `<td class="uline">${значение}</td><td class="mk">${esc(код)}</td></tr>`

/**
 * Официальная печатная форма УПД, статус 1 — счёт-фактура и передаточный документ.
 * Разметка снята с `updOfficialForm` (peleng-portal/src/worker.js:3890–4107).
 *
 * Отличия от оригинала перечислены в шапке blanks1c.ts и печатаются на самом листе:
 * единица измерения не подменяется на «796 шт», флаг «сумма включает НДС» не додумывается,
 * фамилии подписантов не подставляются константами.
 */
export function листУПД(д: Документ1С, условия: УсловияПечати1С = {}): string {
  const вкл = д.суммаВключаетНДС ?? null
  const пок = д.покупатель ?? {}
  const н = недостачаГраф(д.строки, вкл)
  const т = итоги(д.строки, вкл)
  const части = частиДаты(д.дата)

  // ── таблица: 16 граф на строку
  const строки = д.строки.map((с, i) => {
    const р = разложитьСтроку(с, вкл)
    const клетки = ГРАФЫ_УПД.map((г) => {
      const к = клеткаГрафы(г, с, р, i + 1)
      const кл = [г.код === 'А' ? 'c1' : '', г.вид === 'ч' ? 'r' : г.вид === 'л' ? 'l' : '']
        .filter(Boolean).join(' ')
      return `<td${кл ? ` class="${кл}"` : ''}>${к.html}</td>`
    }).join('')
    return `<tr>${клетки}</tr>`
  }).join('')

  const шапкаТаблицы = `<tr>
    <th rowspan="2" class="c1">${ГРАФЫ_УПД[0].титул}</th>
    <th rowspan="2">${ГРАФЫ_УПД[1].титул}</th>
    <th rowspan="2">${ГРАФЫ_УПД[2].титул}</th>
    <th rowspan="2">${ГРАФЫ_УПД[3].титул}</th>
    <th colspan="2">Единица измерения</th>
    ${ГРАФЫ_УПД.slice(6, 13).map((г) => `<th rowspan="2">${г.титул}</th>`).join('')}
    <th colspan="2">Страна происхождения товара</th>
    <th rowspan="2">${ГРАФЫ_УПД[15].титул}</th>
  </tr>
  <tr>
    <th>${ГРАФЫ_УПД[4].титул}</th><th>${ГРАФЫ_УПД[5].титул}</th>
    <th>${ГРАФЫ_УПД[13].титул}</th><th>${ГРАФЫ_УПД[14].титул}</th>
  </tr>
  <tr class="code">${ГРАФЫ_УПД.map((г, i) => `<td${i === 0 ? ' class="c1"' : ''}>${esc(г.код)}</td>`).join('')}</tr>`

  const итогНДС = т.ндс === null ? ПРОЧЕРК : деньги(т.ндс)
  const строкаИтога = `<tr>
    <td class="c1"></td><td colspan="7" class="l b">Всего к оплате (9)</td>
    <td class="r">${деньги(т.база)}</td><td></td><td class="b">Х</td>
    <td class="r">${итогНДС}</td><td class="r">${деньги(т.всего)}</td>
    <td></td><td></td><td></td>
  </tr>`

  // ── что осталось без данных
  const значенияШапки: Record<string, unknown> = {
    '(1)': д.номер, '(1а)': null,
    '(2)': ПРОДАВЕЦ.полное, '(2а)': ПРОДАВЕЦ.адрес, '(2б)': ПРОДАВЕЦ.инн,
    '(3)': 'он же', '(4)': д.грузополучатель ?? пок.наименование,
    '(5)': null, '(5а)': д.номер, '(5б)': null,
    '(6)': пок.наименование, '(6а)': пок.адрес, '(6б)': пок.инн,
    '(7)': д.валюта, '(8)': null,
  }
  const значенияПередачи: Record<string, unknown> = {
    '[8]': д.договор, '[9]': null, '[10]': null, '[11]': д.дата, '[12]': null,
    '[13]': null, '[14]': ПРОДАВЕЦ.полное, '[19]': пок.наименование,
  }
  // Графы [15]–[18] заполняет покупатель от руки — их пустота не наша недостача.
  const наши = ПОЛЯ_ПЕРЕДАЧИ_УПД.filter((п) => !п.из.includes('от руки'))
  const пустыеШапки = пустыеПоля(ПОЛЯ_ШАПКИ_УПД, значенияШапки)
  const пустыеПередачи = пустыеПоля(наши, значенияПередачи)

  const чегоНет: string[] = [
    оговоркаПоГрафам(н),
    ...(пустыеШапки.length
      ? [`Поля шапки без данных: ${пустыеШапки.map((п) => `<b>${esc(п.код)}</b> ${esc(п.титул)}`).join('; ')}.`]
      : []),
    ...(пустыеПередачи.length
      ? [`Поля передаточной части без данных: ${пустыеПередачи.map((п) => `<b>${esc(п.код)}</b> ${esc(п.титул)}`).join('; ')}.`]
      : []),
    esc(ПОЧЕМУ_НЕТ_ПОДПИСАНТОВ),
    esc(КПП_РАСХОЖДЕНИЕ),
    ...(вкл === null
      ? ['<b>Флаг «сумма включает НДС» в документе не найден.</b> Без него из «Суммы» строки'
        + ' не следует ни стоимость без налога, ни итог с налогом: графы 4, 5 и 9 напечатаны'
        + ' прочерком. Портал 55 в этом месте считает, что налог включён, и печатает числа.']
      : []),
    ...(т.неполные
      ? ['<b>Строка «Всего к оплате» неполная:</b> в неё вошли только те строки, которые'
        + ' удалось разложить. Настоящая сумма документа больше напечатанной.']
      : []),
    ...(д.суммаДокумента !== null && д.суммаДокумента !== undefined && т.всего !== null
      && Math.abs(д.суммаДокумента - т.всего) > 0.01
      ? [`<b>Сумма строк расходится с суммой документа:</b> по строкам ${деньги(т.всего)},`
        + ` в шапке документа ${деньги(д.суммаДокумента)}. Разница ${деньги(д.суммаДокумента - т.всего)}.`]
      : []),
    ...(д.помеченНаУдаление ? ['<b>Документ помечен в 1С на удаление.</b>'] : []),
    ...(условия.чегоНет ?? []).map((с) => esc(с)),
  ]

  const чтоЭто = `УПД № ${д.номер || 'без номера'} от ${датаКороткая(д.дата).replace(/<[^>]+>/g, '—')}.`
  const тело = отметкаCRM1С(чтоЭто, чегоНет, условия, д.источник)
    + шапкаУПД(д, пок)
    + `<table class="tt"><colgroup>${ГРАФЫ_УПД.map((г) => `<col style="width:${г.ширина}%">`).join('')}</colgroup>`
    + шапкаТаблицы + строки + строкаИтога + '</table>'
    + подвалУПД(д, пок, части)

  return страница(`УПД ${д.номер ?? ''}`.trim(), тело, СТИЛИ_УПД)
}

/** Шапка бланка: графы (1)–(8) слева и справа от вертикальной черты статуса. */
function шапкаУПД(д: Документ1С, пок: Контрагент1С): string {
  const грузополучатель = естьЗначение(д.грузополучатель)
    ? esc(д.грузополучатель)
    : естьЗначение(пок.наименование) ? esc(пок.наименование) : ПРОЧЕРК
  const иннКппПок = естьЗначение(пок.инн) || естьЗначение(пок.кпп)
    ? `${зн(пок.инн)}/${зн(пок.кпп)}`
    : ПРОЧЕРК

  return `<table class="hdr"><tr>
  <td class="zdiv" style="width:7.52%;font-size:9px;line-height:1.2">Универсальный передаточный документ</td>
  <td style="width:50.5%;padding-left:6px"><table style="width:100%">
    <tr><td class="lbl" style="width:96px">Счет-фактура №</td>
        <td class="uline" style="width:150px">${зн(д.номер)}</td>
        <td class="lbl" style="width:24px">от</td>
        <td class="uline">${датаДлинная(д.дата)}</td><td class="mk">(1)</td></tr>
    <tr><td class="lbl">Исправление №</td><td class="uline">${ПРОЧЕРК}</td>
        <td class="lbl">от</td><td class="uline">${ПРОЧЕРК}</td><td class="mk">(1а)</td></tr>
  </table></td>
  <td class="lbl" style="padding-left:8px;font-size:7.5px">${СТАТУС_УПД.приложение}</td>
</tr></table>

<table class="hdr"><tr>
  <td class="zdiv" style="width:7.52%">
    <div style="font-size:10px;margin:2px 0">Статус:
      <span style="display:inline-block;border:1.4px solid #000;padding:0 7px;font-weight:700">${СТАТУС_УПД.значение}</span></div>
    <div style="font-size:7px;margin-top:4px;line-height:1.15">${СТАТУС_УПД.расшифровка}</div>
  </td>
  <td style="padding:0 2px"><table style="width:100%;table-layout:fixed">
    <colgroup><col style="width:18.1%"><col style="width:35.7%"><col style="width:2.6%">
      <col style="width:13.3%"><col style="width:28.8%"><col style="width:1.5%"></colgroup>
    <tr><td class="lbl b">Продавец:</td><td class="uline">${esc(ПРОДАВЕЦ.полное)}</td><td class="mk">(2)</td>
        <td class="lbl b" style="padding-left:10px">Покупатель:</td>
        <td class="uline">${зн(пок.наименование)}</td><td class="mk">(6)</td></tr>
    <tr><td class="lbl">Адрес:</td><td class="uline">${esc(ПРОДАВЕЦ.адрес)}</td><td class="mk">(2а)</td>
        <td class="lbl" style="padding-left:10px">Адрес:</td>
        <td class="uline">${зн(пок.адрес)}</td><td class="mk">(6а)</td></tr>
    <tr><td class="lbl">ИНН/КПП продавца:</td>
        <td class="uline">${ПРОДАВЕЦ.инн}/${ПРОДАВЕЦ.кпп}</td><td class="mk">(2б)</td>
        <td class="lbl" style="padding-left:10px">ИНН/КПП покупателя:</td>
        <td class="uline">${иннКппПок}</td><td class="mk">(6б)</td></tr>
    <tr><td class="lbl">Грузоотправитель и его адрес:</td><td class="uline">он же</td><td class="mk">(3)</td>
        <td class="lbl" style="padding-left:10px">Валюта: наименование, код</td>
        <td class="uline">${зн(д.валюта)}</td><td class="mk">(7)</td></tr>
    <tr><td class="lbl">Грузополучатель и его адрес:</td><td class="uline">${грузополучатель}</td><td class="mk">(4)</td>
        <td class="lbl" style="padding-left:10px">Идентификатор государственного контракта,
          договора (соглашения) (при наличии):</td>
        <td class="uline">${ПРОЧЕРК}</td><td class="mk">(8)</td></tr>
    <tr><td class="lbl">К платежно-расчетному документу №</td>
        <td class="uline">${ПРОЧЕРК}</td><td class="mk">(5)</td><td colspan="3"></td></tr>
    <tr><td class="lbl">Документ об отгрузке</td>
        <td class="uline" colspan="4">Универсальный передаточный документ,
          № ${зн(д.номер)} от ${датаКороткая(д.дата)}</td><td class="mk">(5а)</td></tr>
    <tr><td class="lbl" colspan="2" style="font-size:7px;line-height:1.05">К счету-фактуре
        (счетам-фактурам), выставленному при получении оплаты, частичной оплаты или иных
        платежей в счет предстоящих поставок: ${ПРОЧЕРК}</td><td class="mk">(5б)</td>
        <td colspan="3"></td></tr>
  </table></td>
</tr></table>`
}

/** Подписи и передаточная часть: графы [8]–[19]. Линейки пустые — см. ПОЧЕМУ_НЕТ_ПОДПИСАНТОВ. */
function подвалУПД(
  д: Документ1С, пок: Контрагент1С, части: ReturnType<typeof частиДаты>,
): string {
  const дата11 = части
    ? `« ${части.дд} »   ${части.мм}   ${части.гг} года`
    : `«      »                    20     года  ${ПРОЧЕРК}`
  const субъектПок = естьЗначение(пок.наименование)
    ? `${esc(пок.наименование)}, ИНН/КПП ${зн(пок.инн)}/${зн(пок.кпп)}`
    : ПРОЧЕРК
  // Три линейки под подпись: должность — подпись — ф.и.о. Ни одна не заполняется данными.
  const линейки = (метка: string): string =>
    `<table style="width:100%;margin-top:8px">
      <tr><td class="uline" style="width:33%"></td><td class="uline" style="width:33%"></td>
          <td class="uline"></td><td class="mk">${метка}</td></tr>
      <tr><td class="upd-cap">(должность)</td><td class="upd-cap">(подпись)</td>
          <td class="upd-cap">(ф.и.о.)</td><td></td></tr>
    </table>`

  return `<table class="hdr" style="margin-top:0"><tr>
  <td class="zdiv" style="width:7.52%;font-size:8px;padding:2px 4px 6px 0">Документ составлен на 1 листе</td>
  <td style="border-bottom:1.7px solid #000;padding-left:6px"><table style="width:100%">
    <tr><td class="lbl" rowspan="2" style="width:20%">Руководитель организации или иное уполномоченное лицо</td>
        <td class="uline" style="width:14%;height:15px"></td><td class="uline" style="width:14%"></td>
        <td class="lbl" rowspan="2" style="width:16%;padding-left:12px">Главный бухгалтер или иное уполномоченное лицо</td>
        <td class="uline" style="width:13%;height:15px"></td><td class="uline" style="width:13%"></td></tr>
    <tr><td class="upd-cap">(подпись)</td><td class="upd-cap">(ф.и.о.)</td>
        <td class="upd-cap">(подпись)</td><td class="upd-cap">(ф.и.о.)</td></tr>
  </table></td>
</tr></table>

<table class="hdr" style="margin-top:6px"><tr><td style="padding:0 2px">
  <table style="width:100%">
    ${строкаШапки('Основание передачи (сдачи) / получения (приемки)', зн(д.договор), '[8]')}
    <tr><td></td><td class="upd-cap" style="text-align:left">(договор; доверенность и др.)</td><td></td></tr>
    ${строкаШапки('Данные о транспортировке и грузе', ПРОЧЕРК, '[9]')}
  </table>
  <table style="width:100%;margin-top:4px"><tr>
    <td style="width:49%;vertical-align:top;border-right:1.7px solid #000;padding-right:8px">
      <div class="lbl">Товар (груз) передал / услуги, результаты работ, права сдал</div>
      ${линейки('[10]')}
      <table style="width:100%;margin-top:6px"><tr>
        <td class="lbl" style="width:150px">Дата отгрузки, передачи (сдачи)</td>
        <td class="uline">${дата11}</td><td class="mk">[11]</td></tr></table>
      <table style="width:100%;margin-top:6px"><tr>
        <td class="lbl">Иные сведения об отгрузке, передаче</td>
        <td class="uline">${ПРОЧЕРК}</td><td class="mk">[12]</td></tr></table>
      <div class="lbl" style="margin-top:6px">Ответственный за правильность оформления факта хозяйственной жизни</div>
      ${линейки('[13]')}
      <table style="width:100%;margin-top:6px">
        <tr><td class="lbl">Наименование экономического субъекта — составителя документа</td></tr>
        <tr><td class="uline">${esc(ПРОДАВЕЦ.полное)}, ИНН/КПП ${ПРОДАВЕЦ.инн}/${ПРОДАВЕЦ.кпп}</td>
            <td class="mk">[14]</td></tr></table>
      <div style="margin-top:8px">М.П.</div>
    </td>
    <td style="width:49%;vertical-align:top;padding-left:8px">
      <div class="lbl">Товар (груз) получил / услуги, результаты работ, права принял</div>
      ${линейки('[15]')}
      <table style="width:100%;margin-top:6px"><tr>
        <td class="lbl" style="width:150px">Дата получения (приемки)</td>
        <td class="uline">«      »                    20     года</td><td class="mk">[16]</td></tr></table>
      <table style="width:100%;margin-top:6px"><tr>
        <td class="lbl">Иные сведения о получении, приемке</td>
        <td class="uline"></td><td class="mk">[17]</td></tr></table>
      <div class="lbl" style="margin-top:6px">Ответственный за правильность оформления факта хозяйственной жизни</div>
      ${линейки('[18]')}
      <table style="width:100%;margin-top:6px">
        <tr><td class="lbl">Наименование экономического субъекта — составителя документа</td></tr>
        <tr><td class="uline">${субъектПок}</td><td class="mk">[19]</td></tr></table>
      <div style="margin-top:8px">М.П.</div>
    </td>
  </tr></table>
</td></tr></table>
<div style="margin-top:8px;font-size:8px;color:#666">${значокПроведения(д.проведён)}
  &middot; Ref_Key в 1С: ${зн(д.ref)}</div>`
}

// ═══════════════════════════ простая таблица (счёт, КП, упрощённый УПД) ═══════════════════════════

/** Клетка простой колонки. Те же правила прочерка, что у граф УПД. */
function клеткаПростая(
  к: КолонкаПростая, стр: СтрокаДокумента1С, р: РазобраннаяСтрока, номер: number,
): Клетка {
  if (к.поле === null) return { html: String(номер), есть: true }
  const г: ГрафаУПД = { код: к.титул, титул: к.титул, ширина: 0, из: к.титул, поле: к.поле }
  return клеткаГрафы(г, стр, р, номер)
}

/** Тело таблицы: строки документа по описанию колонок. */
function телоТаблицы(
  колонки: КолонкаПростая[], строки: СтрокаДокумента1С[], вкл: boolean | null | undefined,
): string {
  return строки.map((с, i) => {
    const р = разложитьСтроку(с, вкл)
    const клетки = колонки.map((к) => {
      const кл = к.вид === 'ч' ? 'r' : к.вид === 'л' ? 'l' : 'c'
      return `<td class="${кл}">${клеткаПростая(к, с, р, i + 1).html}</td>`
    }).join('')
    return `<tr>${клетки}</tr>`
  }).join('')
}

/** Шапка таблицы по описанию колонок. */
const шапкаПростая = (колонки: КолонкаПростая[]): string =>
  '<tr>' + колонки.map((к) => (
    `<th${к.ширина ? ` style="width:${к.ширина}"` : ''}>${esc(к.титул)}</th>`
  )).join('') + '</tr>'

/** «Всего наименований N, на сумму …, в том числе НДС …» + пропись. Общая концовка счёта и КП. */
function итогПрописью(т: Итоги, строк: number, безНДС: boolean): string {
  const пропись = суммаПрописью(т.всего)
  // Точка входит в саму формулировку, а не дописывается снаружи: «руб.» — сокращение,
  // и внешняя точка давала «в том числе НДС 3 960,00 руб..».
  const налог = безНДС
    ? 'НДС не облагается.'
    : т.ндс === null ? `в том числе НДС ${ПРОЧЕРК}.` : `в том числе НДС ${деньги(т.ндс)} руб.`
  return `<div class="sum">Всего наименований ${строк}, на сумму ${деньги(т.всего)} руб. ${налог}</div>`
    + `<div class="b">${пропись ? esc(пропись) : ПРОЧЕРК}</div>`
}

/**
 * Упрощённый лист по документу 1С — аналог `updPrintHTML` (worker.js:3823).
 *
 * Это НЕ бланк 1137, и на листе так написано крупно: девять колонок вместо шестнадцати,
 * без ОКЕИ, страны происхождения и ГТД. Нужен, когда табличную часть из 1С получить не
 * удалось или документ смотрят «для себя»: показать состав и суммы, не притворяясь
 * счётом-фактурой. Отдать вместо УПД его нельзя, и об этом сказано на листе.
 */
export function листУПДпростой(д: Документ1С, условия: УсловияПечати1С = {}): string {
  const вкл = д.суммаВключаетНДС ?? null
  const пок = д.покупатель ?? {}
  const т = итоги(д.строки, вкл)
  const н = недостачаГраф(д.строки, вкл, КОЛОНКИ_ПРОСТЫЕ.map((к) => ({
    код: к.титул, титул: к.титул, ширина: 0, из: к.титул, поле: к.поле,
  })))

  const чегоНет: string[] = [
    '<b>Это не бланк по постановлению № 1137.</b> Здесь девять колонок вместо шестнадцати:'
    + ' нет кода товара, кода вида товара, единицы измерения по ОКЕИ, акциза, страны'
    + ' происхождения и номера декларации на товары. Для налоговой такой лист не годится —'
    + ' печатайте официальную форму.',
    оговоркаПоГрафам(н, КОЛОНКИ_ПРОСТЫЕ.map((к) => ({
      код: к.титул, титул: к.титул, ширина: 0, из: к.титул, поле: к.поле,
    }))),
    ...(вкл === null
      ? ['<b>Флаг «сумма включает НДС» не найден</b> — стоимость без налога и итог с налогом'
        + ' напечатаны прочерком.']
      : []),
    ...(т.неполные ? ['<b>Итог неполный:</b> часть строк разложить не удалось.'] : []),
    ...(условия.чегоНет ?? []).map((с) => esc(с)),
  ]

  const тело = отметкаCRM1С(
    `Состав документа № ${д.номер || 'без номера'} от ${датаКороткая(д.дата).replace(/<[^>]+>/g, '—')}.`,
    чегоНет, условия, д.источник,
  )
    + `<h1>Документ № ${зн(д.номер)} от ${датаКороткая(д.дата)} — состав и суммы</h1>`
    + `<div class="pt">${значокПроведения(д.проведён)}</div>`
    + `<div class="pt"><b>Продавец:</b> ${esc(ПРОДАВЕЦ.полное)}, ИНН ${ПРОДАВЕЦ.инн},`
    + ` КПП ${ПРОДАВЕЦ.кпп}</div>`
    + `<div class="pt"><b>Покупатель:</b> ${зн(пок.наименование)}, ИНН ${зн(пок.инн)},`
    + ` КПП ${зн(пок.кпп)}</div>`
    + `<div class="pt"><b>Основание:</b> ${зн(д.договор)}</div>`
    + `<table>${шапкаПростая(КОЛОНКИ_ПРОСТЫЕ)}${телоТаблицы(КОЛОНКИ_ПРОСТЫЕ, д.строки, вкл)}`
    + `<tr><td colspan="5" class="r b">Итого</td><td class="r b">${деньги(т.база)}</td>`
    + `<td></td><td class="r b">${деньги(т.ндс)}</td><td class="r b">${деньги(т.всего)}</td></tr>`
    + '</table>'
    + итогПрописью(т, д.строки.length, false)

  return страница(`Документ ${д.номер ?? ''} — состав`.trim(), тело)
}

// ═══════════════════════════ договор-счёт ═══════════════════════════

/** Шапка с банковскими реквизитами получателя. Копия таблицы из `sdDocHtml` (worker.js:11010). */
const БАНКОВСКАЯ_ШАПКА = `<table>
  <tr><td colspan="2">${ПРОДАВЕЦ.банк}<div class="cap" style="text-align:left">Банк получателя</div></td>
      <td style="width:150px">БИК</td><td>${ПРОДАВЕЦ.бик}</td></tr>
  <tr><td colspan="2" rowspan="2"></td><td>Сч. №</td><td>${ПРОДАВЕЦ.корсчёт}</td></tr>
  <tr><td>Сч. №</td><td>${ПРОДАВЕЦ.расчётный}</td></tr>
  <tr><td>ИНН ${ПРОДАВЕЦ.инн}</td><td>КПП ${ПРОДАВЕЦ.кпп}</td>
      <td colspan="2">${esc(ПРОДАВЕЦ.краткое)}<div class="cap" style="text-align:left">Получатель</div></td></tr>
</table>`

/**
 * Договор-счёт: счёт на оплату, одновременно являющийся офертой. Разметка снята с
 * `sdDocHtml` (peleng-portal/src/worker.js:10897–11033) — это самая полная форма счёта из
 * найденных: в брифинге своей вёрстки счёта нет вовсе, там счёт рисует генератор документов
 * Bitrix (`crm.documentgenerator.document.add`).
 *
 * Юридические пункты и блок про сверхурочные перенесены ДОСЛОВНО (см. blanks1c.ts):
 * переписывать текст оферты своими словами нельзя.
 *
 * Подписи «Генеральный директор Ильин А. П. / Бухгалтер Ершова Е. Г.» портал печатает
 * константами. Здесь это линейки: счёт подписывают от руки, а фамилия из константы CRM на
 * денежном документе — ровно то враньё, ради которого написан этот файл.
 */
export function листСчёта(д: Документ1С, условия: УсловияПечати1С = {}): string {
  const вкл = д.суммаВключаетНДС ?? null
  const пок = д.покупатель ?? {}
  const т = итоги(д.строки, вкл)
  const графы = КОЛОНКИ_СЧЁТА.map((к) => ({
    код: к.титул, титул: к.титул, ширина: 0, из: к.титул, поле: к.поле,
  }))
  const н = недостачаГраф(д.строки, вкл, графы)
  // «Без НДС» по всему счёту — только когда ставка известна и она «без НДС» во всех строках.
  const безНДС = д.строки.length > 0
    && д.строки.every((с) => процентСтавки(с.ставка) === null)

  const покупательСтрока = естьЗначение(пок.наименование)
    ? [esc(пок.наименование),
      естьЗначение(пок.инн) ? `ИНН ${esc(пок.инн)}` : `ИНН ${ПРОЧЕРК}`,
      естьЗначение(пок.кпп) ? `КПП ${esc(пок.кпп)}` : `КПП ${ПРОЧЕРК}`,
      естьЗначение(пок.адрес) ? esc(пок.адрес) : `адрес ${ПРОЧЕРК}`].join(', ')
    : ПРОЧЕРК

  const пункты = ПУНКТЫ_ДОГОВОРА_СЧЁТА
    .map((т2, i) => `<tr><td class="c" style="width:24px">${i + 1}</td><td>${esc(т2)}</td></tr>`)
    .join('')
  const работы = '<div style="margin-top:10px"><b>В случае, если будут работы по ремонту,'
    + ' монтажу и/или замене судового оборудования, необходимо добавить:</b>'
    + БЛОК_РАБОТ_ДОГОВОРА_СЧЁТА.map((т2) => `<div class="pt">${esc(т2)}</div>`).join('')
    + '</div>'

  const чегоНет: string[] = [
    оговоркаПоГрафам(н, графы),
    ...(естьЗначение(пок.наименование)
      ? []
      : ['<b>Покупатель не определён</b> — в шапке счёта прочерк. Счёт с прочерком вместо'
        + ' заказчика в банк не примут: заполните реквизиты в 1С.']),
    ...(естьЗначение(д.договор)
      ? []
      : ['<b>Основание не заполнено</b> в документе 1С (ни «Основание», ни договор'
        + ' контрагента) — в счёте прочерк. Управляемого флага «Печатать основание» из'
        + ' ТЗ_Печать_основания_1С.md в документах 1С ещё нет, поэтому строка печатается'
        + ' всегда: скрыть её решением CRM значило бы решить за бухгалтерию.']),
    ...(вкл === null
      ? ['<b>Флаг «сумма включает НДС» не найден:</b> колонки «Цена без НДС» и «Сумма с НДС»'
        + ' напечатаны прочерком, потому что из одной «Суммы» строки они не выводятся.']
      : []),
    ...(т.неполные
      ? ['<b>Итог неполный:</b> часть строк разложить не удалось, настоящая сумма больше.']
      : []),
    'Реквизиты продавца (банк, р/с, к/с, БИК, ИНН/КПП, юр. адрес) взяты из констант CRM,'
    + ' а не из 1С: справочник организаций в зеркале не хранится. Источник констант и дата'
    + ' сверки — в комментарии к <code>ПРОДАВЕЦ</code> в lib/blanks1c.ts.',
    esc(КПП_РАСХОЖДЕНИЕ),
    'Подписи не проставлены: ФИО подписантов CRM недоступны (см. ниже), счёт подписывается от руки.',
    esc(ПОЧЕМУ_НЕТ_ПОДПИСАНТОВ),
    ...(условия.чегоНет ?? []).map((с) => esc(с)),
  ]

  const тело = отметкаCRM1С(
    `Договор-счёт № ${д.номер || 'без номера'}.`, чегоНет, условия, д.источник,
  )
    + БАНКОВСКАЯ_ШАПКА
    + `<h1>Договор-счёт № ${зн(д.номер)} от ${датаКороткая(д.дата)}</h1>`
    + `<div class="pt"><b>Поставщик (Исполнитель):</b> ${esc(ПРОДАВЕЦ.полное)},`
    + ` ИНН ${ПРОДАВЕЦ.инн}, КПП ${ПРОДАВЕЦ.кпп}, ${esc(ПРОДАВЕЦ.адрес)},`
    + ` тел.: ${esc(ПРОДАВЕЦ.телефон)}</div>`
    + `<div class="pt"><b>Покупатель (Заказчик):</b> ${покупательСтрока}</div>`
    // Основание — отдельная строка счёта, а не подробность: по ней счёт связывают с
    // договором в учёте (см. ТЗ_Печать_основания_1С.md). Пусто — прочерк: строка, которой
    // на листе просто нет, читается как «счёт без договора», а это другое утверждение.
    + `<div class="pt"><b>Основание:</b> ${зн(д.договор)}</div>`
    + '<div class="pt">В соответствии с настоящим Договором-счётом Поставщик (Исполнитель)'
    + ' обязуется выполнить работы/оказать услуги/поставить оборудование указанные далее:</div>'
    + `<table>${шапкаПростая(КОЛОНКИ_СЧЁТА)}${телоТаблицы(КОЛОНКИ_СЧЁТА, д.строки, вкл)}</table>`
    + итогПрописью(т, д.строки.length, безНДС)
    + `<table style="margin-top:8px"><tbody>${пункты}</tbody></table>`
    + работы
    + '<div class="sign">'
    + '<div style="flex:1"><div class="uline" style="height:18px"></div>'
    + '<div class="cap" style="text-align:left">Генеральный директор — должность, подпись, ф.и.о.</div></div>'
    + '<div style="flex:1"><div class="uline" style="height:18px"></div>'
    + '<div class="cap" style="text-align:left">Бухгалтер — должность, подпись, ф.и.о.</div></div>'
    + '</div>'

  return страница(`Договор-счёт ${д.номер ?? ''}`.trim(), тело)
}

// ═══════════════════════════ коммерческое предложение ═══════════════════════════

/** КП собирается не из документа 1С, а из позиций и условий, которые набрал человек. */
export interface КоммерческоеПредложение {
  номер?: string | null
  /** ISO-дата. Пусто — прочерк: «сегодня» подставлять нельзя, КП датируют вручную. */
  дата?: string | null
  заказчик?: Контрагент1С | null
  строки: СтрокаДокумента1С[]
  /** Включают ли цены позиций НДС. */
  ценыСНДС?: boolean | null
  условияОплаты?: string | null
  условияПоставки?: string | null
  прочиеУсловия?: string | null
  /** Оферта или нет — от этого зависит юридический абзац. */
  оферта?: boolean
  /** Кто составил: имя и почта. Пусто — линейка под подпись. */
  составил?: { имя?: string | null; почта?: string | null } | null
  /**
   * Скидка на всё предложение, %. Пусто или 0 — скидки нет и строки о ней на листе тоже.
   *
   * ПОЧЕМУ ПРОЦЕНТ, А НЕ РУБЛИ. Скидка в конструкторе применяется К СУММЕ, а не к строке
   * (см. `итогиКП` в lib/prices.ts), и всё, что за ней следует, — линейно: и налог, и итог
   * умножаются на один и тот же множитель. Передавая процент, лист считает и «сколько
   * скидки», и «сколько НДС после неё» сам, из своих же чисел, — вместо того чтобы верить
   * четырём цифрам, присланным экраном, и не иметь возможности их проверить.
   */
  скидкаПроцент?: number | null
  /**
   * Итог, посчитанный экраном. Только для СВЕРКИ: лист печатает свой, а расхождение
   * выносит в оговорку с обоими числами — как расхождение суммы строк с суммой документа
   * в УПД. Экран складывает округлённые цены и округляет один раз, лист — построчно, и
   * копейка разницы возможна; молчать о ней нельзя, подставлять чужое число тоже.
   */
  итогЭкрана?: number | null
}

/**
 * Коммерческое предложение. Разметка снята с `kpDocHtml` (worker.js:10919–10967).
 *
 * Единственная из трёх форм, которой 1С не нужна вовсе: позиции набирает человек. Поэтому
 * служебная отметка здесь короче — врать нечем, кроме сумм, и за суммами следит тот же
 * `итоги`, что и на счёте.
 */
export function листКП(кп: КоммерческоеПредложение, условия: УсловияПечати1С = {}): string {
  const вкл = кп.ценыСНДС ?? null
  const зак = кп.заказчик ?? {}
  const т = итоги(кп.строки, вкл)
  const графы = КОЛОНКИ_КП.map((к) => ({
    код: к.титул, титул: к.титул, ширина: 0, из: к.титул, поле: к.поле,
  }))
  const н = недостачаГраф(кп.строки, вкл, графы)
  const безНДС = кп.строки.length > 0 && кп.строки.every((с) => процентСтавки(с.ставка) === null)

  // ── скидка: один множитель на налог и на итог, потому что дальше всё линейно
  const проц = typeof кп.скидкаПроцент === 'number' && Number.isFinite(кп.скидкаПроцент)
    ? Math.min(100, Math.max(0, кп.скидкаПроцент))
    : 0
  const доля = 1 - проц / 100
  const скидкаРуб = проц > 0 && т.линии !== null ? окр2(т.линии * (1 - доля)) : null
  const ндсКОплате = т.ндс === null ? null : окр2(т.ндс * доля)
  const итогКОплате = т.всего === null ? null : окр2(т.всего * доля)
  /** Итоги в том виде, в котором они уходят в пропись: уже за вычетом скидки. */
  const тК: Итоги = { ...т, ндс: ндсКОплате, всего: итогКОплате }
  const расхождение = typeof кп.итогЭкрана === 'number' && Number.isFinite(кп.итогЭкрана)
    && итогКОплате !== null && Math.abs(кп.итогЭкрана - итогКОплате) > 0.01
    ? кп.итогЭкрана - итогКОплате
    : null

  const почта = естьЗначение(кп.составил?.почта) ? esc(кп.составил?.почта) : esc(ПРОДАВЕЦ.почта)
  const оферта = кп.оферта !== false
  const блокЗаказчика = естьЗначение(зак.наименование)
    ? `<div class="b">${esc(зак.наименование)}</div>`
      + (естьЗначение(зак.инн) ? `<div>ИНН: ${esc(зак.инн)}${естьЗначение(зак.кпп) ? `, КПП: ${esc(зак.кпп)}` : ''}</div>` : '')
      + (естьЗначение(зак.адрес) ? `<div>Адрес: ${esc(зак.адрес)}</div>` : '')
    : `<div class="b">Заказчик ${ПРОЧЕРК}</div>`

  const условие = (метка: string, v?: string | null): string =>
    естьЗначение(v) ? `<div class="pt"><b>${метка}:</b> ${esc(v)}</div>` : ''
  // Ни одного условия не заполнено — под заголовком «Условия и комментарии» встаёт прочерк.
  // Пустое место под заголовком читается как «условий нет», а это другое утверждение:
  // «условия не заполнены» и «условий не предусмотрено» клиент понимает по-разному.
  const естьУсловия = естьЗначение(кп.условияОплаты) || естьЗначение(кп.условияПоставки)
    || естьЗначение(кп.прочиеУсловия)

  const чегоНет: string[] = [
    оговоркаПоГрафам(н, графы),
    ...(вкл === null
      ? ['<b>Не указано, включают ли цены НДС</b> — колонка «Сумма» и итог напечатаны'
        + ' прочерком. Это выбор человека, а не пропуск данных: отметьте его перед печатью.']
      : []),
    ...(т.неполные ? ['<b>Итог неполный:</b> часть позиций разложить не удалось.'] : []),
    ...(естьЗначение(кп.дата) ? [] : ['Дата КП не указана — в шапке прочерк.']),
    ...(естьЗначение(зак.наименование)
      ? []
      : ['<b>Заказчик не указан</b> — в блоке реквизитов прочерк. КП без адресата отправлять'
        + ' некому: впишите название клиента.']),
    ...(естьУсловия
      ? []
      : ['Условия оплаты, поставки и прочие условия не заполнены — под заголовком стоит'
        + ' прочерк. Это не «условий нет», а «их не написали»: допишите перед отправкой.']),
    ...(проц > 0
      ? [`<b>Скидка ${esc(String(проц))} %</b> посчитана от всей суммы предложения и вычтена`
        + ' из итога; в цены позиций она не входит — в колонке «Сумма» стоят суммы ДО'
        + ' скидки. НДС уменьшен в той же доле: скидка применяется до налога, иначе налог'
        + ' считался бы от суммы, которую клиент не платит.']
      : []),
    ...(расхождение !== null
      ? [`<b>Итог листа расходится с итогом на экране конструктора:</b> на листе`
        + ` ${деньги(итогКОплате)}, на экране ${деньги(кп.итогЭкрана as number)},`
        + ` разница ${деньги(расхождение)}. Экран складывает округлённые цены и округляет`
        + ' налог один раз на всю сумму, лист — построчно. Перед отправкой сверьтесь.']
      : []),
    'Цены и сроки в КП — предложение, а не обязательство 1С: этот лист ни с каким документом'
    + ' в 1С не связан и в 1С не попадает.',
    ...(условия.чегоНет ?? []).map((с) => esc(с)),
  ]

  const тело = отметкаCRM1С(
    `Коммерческое предложение № ${кп.номер || 'без номера'}.`, чегоНет, условия, 'без-1с',
  )
    + '<div style="display:flex;justify-content:space-between;gap:20px;'
    + 'border-bottom:1px solid #1f6fb0;padding-bottom:8px">'
    + `<div style="color:#1f6fb0;font-weight:800;font-size:15px">${esc(ПРОДАВЕЦ.краткое)}`
    + `<div style="color:#555;font-weight:400;font-size:10.5px;margin-top:2px">${почта}`
    + ` &middot; ${esc(ПРОДАВЕЦ.телефон)}</div></div>`
    + `<div style="text-align:right;font-weight:700;font-size:11px">${esc(ПРОДАВЕЦ.краткое)}<br>`
    + `${esc(ПРОДАВЕЦ.адрес)}</div></div>`
    + `<h1>КОММЕРЧЕСКОЕ ПРЕДЛОЖЕНИЕ № ${зн(кп.номер)} от ${датаКороткая(кп.дата)}</h1>`
    + '<div class="pt b">Условия и комментарии:</div>'
    + (естьУсловия
      ? условие('Условия оплаты', кп.условияОплаты)
        + условие('Условия поставки', кп.условияПоставки)
        + условие('Прочие условия', кп.прочиеУсловия)
      : `<div class="pt">${ПРОЧЕРК} условия оплаты, поставки и прочие не заполнены</div>`)
    + `<div class="pt"${оферта ? '' : ' style="text-align:center;font-weight:600"'}>`
    + `${esc(оферта ? ОФЕРТА_КП.оферта : ОФЕРТА_КП.не_оферта)}</div>`
    + '<div class="pt">Здравствуйте! В соответствии с нашей договорённостью мы рады предложить'
    + ' вам следующие товары и услуги:</div>'
    + `<table>${шапкаПростая(КОЛОНКИ_КП)}${телоТаблицы(КОЛОНКИ_КП, кп.строки, вкл)}`
    + (скидкаРуб !== null
      ? `<tr><td colspan="6" class="r b">Сумма по позициям</td>`
        + `<td class="r">${деньги(т.линии)}</td></tr>`
        + `<tr><td colspan="6" class="r b">Скидка ${esc(String(проц))} %</td>`
        + `<td class="r">−${деньги(скидкаРуб)}</td></tr>`
      : '')
    + `<tr><td colspan="6" class="r b">НДС</td><td class="r">${безНДС ? 'не облагается' : деньги(ндсКОплате)}</td></tr>`
    + `<tr><td colspan="6" class="r b">Итого</td><td class="r b">${деньги(итогКОплате)}</td></tr></table>`
    + итогПрописью(тК, кп.строки.length, безНДС)
    + '<div style="display:flex;gap:20px;margin-top:14px;font-size:10.5px">'
    + `<div style="flex:1"><div class="b">${esc(ПРОДАВЕЦ.краткое)}</div>`
    + `<div>Адрес: ${esc(ПРОДАВЕЦ.адрес)}</div><div>Телефон: ${esc(ПРОДАВЕЦ.телефон)}</div>`
    + `<div>E-mail: ${почта}</div><div>ИНН: ${ПРОДАВЕЦ.инн}</div></div>`
    + `<div style="flex:1">${блокЗаказчика}</div></div>`
    + '<div class="sign"><div style="flex:1">С уважением,<div class="uline" style="height:18px;margin-top:18px"></div>'
    + `<div class="cap" style="text-align:left">ИСПОЛНИТЕЛЬ — подпись, ф.и.о.`
    + `${естьЗначение(кп.составил?.имя) ? ` (составил: ${esc(кп.составил?.имя)})` : ''}</div></div>`
    + '<div style="flex:1"><div class="uline" style="height:18px;margin-top:18px"></div>'
    + '<div class="cap" style="text-align:left">ЗАКАЗЧИК — подпись, ф.и.о.</div></div></div>'

  return страница(`КП ${кп.номер ?? ''}`.trim(), тело)
}

// ─────────────────── КП из «Конструктора КП» ───────────────────
//
// Мост между экраном (pages/QuoteBuilder.tsx) и бланком. Отдельной функцией, а не кодом
// внутри экрана, по той же причине, по какой lib/prices.ts отделён от экрана: здесь
// решается, что печатается прочерком, а это надо проверять без отрисовки.

/**
 * Ставка НДС конструктора (число процентов) в строку, как её пишет 1С.
 *
 * Незнакомую ставку НЕ подгоняем: `НДС15` листу неизвестен, `процентСтавки` вернёт
 * `undefined`, графа станет прочерком, а строка уйдёт в оговорку. Именно этого мы и хотим —
 * ровно на подгонке незнакомой ставки под ноль сломан `INV_VAT_PCT` порталов.
 */
const ставка1С = (проценты: number): string => (проценты === 0 ? 'БезНДС' : `НДС${проценты}`)

/** Что печатать на листе и что сказать о неполноте. */
export interface КПКонструктора {
  кп: КоммерческоеПредложение
  /** Оговорки для служебной отметки: их дописывает вызывающий к своим. */
  чегоНет: string[]
}

/**
 * Собрать печатное КП из того, что набрал человек в конструкторе.
 *
 * ЧТО ЗДЕСЬ ВАЖНО. Позиция без цены НЕ выбрасывается: она едет на лист с прочерком в
 * колонках «Цена» и «Сумма» и отдельной строкой в оговорке. Выбросить её значило бы отдать
 * клиенту предложение без части позиций и не сказать об этом; подставить ноль — занизить
 * предложение молча. Ровно так же поступает `итогиКП`: такие строки в итог не входят.
 */
export function кпИзКонструктора(
  строки: СтрокаКП[], у: УсловияКП, ш: ШапкаКП,
  доп: { дата?: string | null; оферта?: boolean } = {},
): КПКонструктора {
  const и = итогиКП(строки, у)
  const ставка = ставка1С(у.vatRate)

  const позиции: СтрокаДокумента1С[] = строки.map((с) => {
    const p = ценаСтроки(с, у)
    return {
      вид: 'товар',
      // Артикул отдельной колонки в бланке КП нет — он идёт в наименование, как в тексте
      // письма (`текстКП`), чтобы клиент мог сослаться на позицию.
      наименование: с.pn ? `${с.name} [${с.pn}]` : с.name,
      едИмя: с.unit || null,
      количество: с.qty,
      ценаСырая: p,
      сумма: p === null ? null : копейки(p * с.qty),
      ставка,
    }
  })

  const безЦены = строки.filter((с) => ценаСтроки(с, у) === null)
  const спометками = строки.filter((с) => с.problems.length > 0)

  const чегоНет: string[] = []
  if (безЦены.length) {
    const причины = [...new Set(безЦены.map(
      (с) => почемуНеГодна({ price_user: с.base, cur_user: с.cur, problems: с.problems })
        || 'цена не введена вручную',
    ))]
    чегоНет.push(
      `У ${безЦены.length} ${plural(безЦены.length, 'позиции', 'позиций', 'позиций')} цены`
      + ' нет: они напечатаны прочерком и в итог НЕ вошли, итог меньше настоящего.'
      + ` Причины: ${причины.join('; ')}.`,
    )
  }
  if (спометками.length) {
    чегоНет.push(
      `У ${спометками.length} ${plural(спометками.length, 'позиции', 'позиций', 'позиций')}`
      + ' есть пометки прайса (расхождения цен, подозрительная цена и т. п.). На листе их'
      + ' не видно — они показаны на экране конструктора рядом с ценой.',
    )
  }
  if (у.markupPct > 0) {
    чегоНет.push(
      `Наценка ${у.markupPct} % уже включена в цены позиций и отдельной строкой не показана.`
      + ' На цены, введённые вручную, она не применяется.',
    )
  }
  if (у.vatRate !== 0 && процентСтавки(ставка) === undefined) {
    чегоНет.push(
      `Ставка НДС ${у.vatRate} % листу неизвестна (в 1С такой строки ставки нет):`
      + ' в колонке «Ставка НДС» прочерк. Сумма налога при этом посчитана конструктором.',
    )
  }
  чегоНет.push(
    'Реквизиты заказчика (ИНН, КПП, адрес) в конструкторе не вводятся — в блоке реквизитов'
    + ' только название. Подставлять их из сделки CRM пока нечем.',
  )

  return {
    кп: {
      номер: ш.номер || null,
      дата: доп.дата || null,
      заказчик: ш.клиент ? { наименование: ш.клиент } : null,
      строки: позиции,
      ценыСНДС: у.vatMode === 'gross',
      условияОплаты: ш.условияОплаты || null,
      условияПоставки: ш.условияПоставки || null,
      прочиеУсловия: ш.прочее || null,
      оферта: доп.оферта !== false,
      составил: { имя: ш.менеджер || null, почта: ш.почта || null },
      скидкаПроцент: у.discountPct || null,
      итогЭкрана: и.итог,
    },
    чегоНет,
  }
}

// ═══════════════════════════ выгрузка ═══════════════════════════

export type ИсходВыгрузки = 'ok' | 'нет-браузера'

/**
 * Сохранить лист файлом .doc. Тот же приём, что у `docOut` (worker.js:10968): Blob с типом
 * application/msword и HTML внутри. Word открывает такой файл и даёт править — этого от
 * выгрузки и ждут (в счёт руками дописывают строку, в КП — абзац).
 *
 * BOM (﻿) в начале обязателен: без него Word читает файл в системной кодировке и весь
 * русский текст превращается в вопросительные знаки.
 *
 * Серверного PDF у нас нет и не появится: рисовать PDF нечем, а печать через окно браузера
 * даёт «Сохранить в PDF» бесплатно.
 */
export function скачатьДок(
  html: string, имя: string,
  док: Document | null = typeof document === 'undefined' ? null : document,
): ИсходВыгрузки {
  if (!док || typeof Blob === 'undefined' || typeof URL === 'undefined'
    || typeof URL.createObjectURL !== 'function') return 'нет-браузера'
  const blob = new Blob(['﻿' + html], { type: 'application/msword' })
  const url = URL.createObjectURL(blob)
  const a = док.createElement('a')
  a.href = url
  // Слэши и двоеточия в имени документа 1С встречаются: браузер такой download молча уронит.
  a.download = `${имя.replace(/[\\/:*?"<>|]+/g, '-').trim() || 'документ'}.doc`
  док.body.appendChild(a)
  a.click()
  док.body.removeChild(a)
  if (typeof URL.revokeObjectURL === 'function') URL.revokeObjectURL(url)
  return 'ok'
}

// ═══════════════════════════ сборка документа из 1С ═══════════════════════════

/** Нулевой GUID 1С означает «ссылка не заполнена». Пустой строкой она не бывает. */
const ПУСТОЙ_GUID = '00000000-0000-0000-0000-000000000000'

/** Ссылка заполнена? Нулевой GUID — это «нет связи», а не значение. */
export const ссылкаЕсть = (v: unknown): v is string =>
  typeof v === 'string' && v.length === 36 && v !== ПУСТОЙ_GUID

/** Число из ответа 1С. Деньги приезжают числом, но `null` и строки тоже встречаются. */
function число1С(v: unknown): number | null {
  if (typeof v === 'number') return Number.isFinite(v) ? v : null
  if (typeof v === 'string' && v.trim()) {
    // «515000|RUB» — так Bitrix пишет деньги; в 1С такого нет, но парсер не должен падать.
    const n = Number(v.split('|')[0])
    return Number.isFinite(n) ? n : null
  }
  return null
}

/** Юр. адрес контрагента: первая строка КонтактнойИнформации с типом «Адрес». */
export function адресКонтрагента(зап: Record<string, unknown> | null | undefined): string | null {
  const ки = (зап?.КонтактнаяИнформация ?? []) as Array<Record<string, unknown>>
  if (!Array.isArray(ки)) return null
  const непусто = (r: Record<string, unknown>) => String(r?.Представление ?? '').trim()
  const стр = ки.find((r) => r && r.Тип === 'Адрес' && непусто(r)) ?? ки.find((r) => r && непусто(r))
  return стр ? непусто(стр) : null
}

/** Что понадобилось сборке, но не приехало. Уходит в служебную отметку листа. */
export interface ПропускиСборки {
  /** Справочники, которые не ответили: строки без них печатаются прочерком. */
  несобрано: string[]
}

/** Ответ сборщика: документ, свежесть худшего из ответов и список пропусков. */
export interface СобранныйДокумент {
  документ: Документ1С
  свежесть: OdataMeta | null
  пропуски: ПропускиСборки
}

/** Один запрос к 1С. Тип сужен до записи: разбираем поля по именам из ответа. */
type Запрос1С = (путь: string) => Promise<{ rows: Array<Record<string, unknown>>; meta: OdataMeta }>

/** `Ref_Key eq guid'a' or Ref_Key eq guid'b'` — 1С не понимает `in`, только цепочку `or`. */
const фильтрПоКлючам = (поле: string, ключи: string[]): string =>
  ключи.map((k) => `${поле} eq guid'${k}'`).join(' or ')

/**
 * Справочник «ключ → запись» одним запросом на пачку. Пачки по 12 ссылок: длинный OR-фильтр
 * 1С отвергает, и это уже проверено в lib/purchasing.ts (`refNamePaths`).
 *
 * `$select` НЕ используем даже там, где поля латиницей: прокси не перекодирует путь, а
 * кириллица в `$select` даёт от 1С 503 (проверено на `Catalog_Организации` 28.07.2026).
 * Лишние поля дешевле сломанного запроса.
 */
async function справочник(
  запрос: Запрос1С, сущность: string, ключи: string[],
): Promise<{ карта: Map<string, Record<string, unknown>>; meta: OdataMeta | null; сбой: boolean }> {
  const уник = [...new Set(ключи.filter(ссылкаЕсть))]
  const карта = new Map<string, Record<string, unknown>>()
  if (!уник.length) return { карта, meta: null, сбой: false }
  let meta: OdataMeta | null = null
  let сбой = false
  for (let i = 0; i < уник.length; i += 12) {
    const часть = уник.slice(i, i + 12)
    try {
      const r = await запрос(
        `${сущность}?$format=json&$top=50&$filter=${encodeURIComponent(фильтрПоКлючам('Ref_Key', часть))}`,
      )
      meta = r.meta
      for (const зап of r.rows) {
        const k = String(зап.Ref_Key ?? '')
        if (k) карта.set(k, зап)
      }
    } catch {
      // Справочник не ответил — строки останутся с прочерком, и лист скажет об этом.
      // Валить всю печать из-за ненайденной страны происхождения нельзя.
      сбой = true
    }
  }
  return { карта, meta, сбой }
}

/** Документа с таким Ref_Key в базе нет. Отдельный тип: экран обязан сказать это словами. */
export class ДокументНеНайден extends Error {
  constructor(public ref: string, public база: string) {
    super(`Документ ${ref} в базе 1С «${база}» не найден`)
    this.name = 'ДокументНеНайден'
  }
}

const текст = (v: unknown): string | null => {
  const s = String(v ?? '').trim()
  return s ? s : null
}

/** Первое непустое поле из списка имён: у БП и УТ поля называются по-разному. */
const первое = (зап: Record<string, unknown>, имена: string[]): unknown => {
  for (const и of имена) if (зап[и] !== undefined && зап[и] !== null && зап[и] !== '') return зап[и]
  return null
}

/**
 * Чем базы 1С отличаются друг от друга для печати. Не косметика: у одного и того же
 * документа в УТ и БП РАЗНЫЕ имена полей, и часть справочников ядро для УТ не выпускает.
 *
 * КАКАЯ БАЗА НАША И ПОЧЕМУ ОФИЦИАЛЬНЫЙ УПД ИЗ НЕЁ НЕ ВЫХОДИТ. Зеркало `ut_sales` отражает
 * УТ. Проверено живыми GET 28.07.2026, и обе проверки отрицательные:
 *   • по ссылке: `Ref_Key` реализации 2500-001306 находится в `ut` и НЕ находится в `bp`
 *     (БП отвечает `value: []`) — базы независимы, GUID у них свои;
 *   • по номеру: в БП нет документа с номером `2500-001306` (0 совпадений), потому что у
 *     БП собственная нумерация вида `26ПРД07/065`.
 * Моста УТ → БП, стало быть, нет ни одного. Кнопка со страницы «Отгрузка» ходит в `ut` и
 * мирится с тем, что часть граф бланка оттуда получить нельзя вовсе. Полный бланк 1137
 * собирается только по документу, ref которого взят из самой БП, — а списка документов БП
 * в CRM сегодня нет: они не зеркалятся.
 *
 * `null` в поле справочника означает «этой сущности в базе нет или ядро её не выпускает».
 * Такая графа печатается прочерком, а причина уходит в оговорку — молча оставлять пустую
 * клетку нельзя, а лезть в заведомо запрещённую сущность бессмысленно (ядро ответит
 * forbidden_entity и только потратит запрос).
 */
interface Схема1С {
  флагНалога: string[]
  основание: string[]
  договорКлюч: string
  единицаКлюч: string
  ставкаПоле: string | null
  справочникЕдиниц: string | null
  справочникСтран: string | null
  справочникГТД: string | null
  справочникДоговоров: string | null
  /** Чего в этой базе не достать — прямо в оговорку листа. */
  недоступно: string[]
}

const СХЕМЫ: Record<'1c:ut' | '1c:bp', Схема1С> = {
  '1c:bp': {
    флагНалога: ['СуммаВключаетНДС'],
    основание: ['ОснованиеПередачи'],
    договорКлюч: 'ДоговорКонтрагента_Key',
    единицаКлюч: 'ЕдиницаИзмерения_Key',
    ставкаПоле: 'СтавкаНДС',
    справочникЕдиниц: 'Catalog_КлассификаторЕдиницИзмерения',
    справочникСтран: 'Catalog_СтраныМира',
    справочникГТД: 'Catalog_НомераГТД',
    справочникДоговоров: 'Catalog_ДоговорыКонтрагентов',
    недоступно: [],
  },
  '1c:ut': {
    флагНалога: ['ЦенаВключаетНДС', 'СуммаВключаетНДС'],
    основание: ['Основание', 'ОснованиеПередачи'],
    договорКлюч: 'Договор_Key',
    // В строке УТ единица измерения — это Упаковка_Key, а справочник упаковок ядро наружу
    // не выпускает (его нет в ODATA_ENTITIES['ut'], core_api.py:2455).
    единицаКлюч: 'Упаковка_Key',
    // СтавкаНДС в УТ — ссылка (СтавкаНДС_Key) на справочник, которого в белом списке нет.
    // Поэтому графа 7 остаётся прочерком, а графа 8 берётся из настоящей СуммаНДС строки.
    ставкаПоле: null,
    справочникЕдиниц: null,
    справочникСтран: null,
    справочникГТД: null,
    справочникДоговоров: null,
    недоступно: [
      'графы 2 и 2а (единица измерения по ОКЕИ): в строке УТ вместо неё Упаковка_Key,'
      + ' а справочник упаковок ядро наружу не выпускает',
      'графа 7 (налоговая ставка): в УТ это ссылка на справочник ставок, которого нет'
      + ' в белом списке прокси. Сумма налога в графе 8 при этом настоящая — она приходит'
      + ' полем СуммаНДС самой строки',
      'графы 10, 10а и 11 (страна происхождения и номер ГТД): таких полей в табличной части'
      + ' УТ нет вовсе — они есть только в базе БП',
    ],
  },
}

/**
 * Собрать реализацию (УПД) целиком: шапка, обе табличные части и все справочники.
 *
 * ПОЧЕМУ ЭТО ЗДЕСЬ, А НЕ В api/. Потому что это не транспорт, а сборка бланка: какие поля
 * куда идут и что делать, когда справочник промолчал, — вопрос печатной формы. Транспорт
 * передаётся параметром `запрос` (в приложении это `odataGet` из api/odata.ts), и благодаря
 * этому всю сборку можно проверить без сети — см. tests/print1c.test.ts.
 *
 * ЗАПРОСОВ ПОЛУЧАЕТСЯ МНОГО, И ЭТО ОСОЗНАННО. Три на документ и до шести на справочники.
 * Дешевле не выходит: в зеркале от реализации есть только шапка, а справочников 1С
 * (номенклатура, ОКЕИ, страны, ГТД, контрагенты, договоры) в зеркале нет ни одного. Зато
 * ходим только по нажатию «Печать», а не при открытии карточки.
 */
export async function собратьРеализацию(
  запрос: Запрос1С, ref: string, база: '1c:ut' | '1c:bp' = '1c:bp',
): Promise<СобранныйДокумент> {
  const сх = СХЕМЫ[база]
  const сущность = 'Document_РеализацияТоваровУслуг'
  const фильтр = encodeURIComponent(`Ref_Key eq guid'${ref}'`)
  const шапка = await запрос(`${сущность}?$format=json&$filter=${фильтр}`)
  const док = шапка.rows[0]
  if (!док) throw new ДокументНеНайден(ref, база)

  const несобрано: string[] = [...сх.недоступно]

  /**
   * Табличная часть. 1С отдаёт её ВЛОЖЕННОЙ в документ при выборке по $filter (проверено
   * на живом ответе УТ 28.07.2026: `Товары` — массив из 6 строк прямо в шапке). Отдельный
   * запрос к `Document_X_Товары` нужен только когда вложенного массива нет: лишний поход
   * в 1С за тем, что уже приехало, — это секунда ожидания на пустом месте.
   */
  const часть = async (имя: string): Promise<Array<Record<string, unknown>>> => {
    const вложено = док[имя]
    if (Array.isArray(вложено)) return вложено as Array<Record<string, unknown>>
    try {
      const r = await запрос(`${сущность}_${имя}?$format=json&$top=500&$filter=${фильтр}`)
      return r.rows
    } catch {
      несобрано.push(`табличная часть «${имя}» — 1С не ответила, этих строк на листе нет`)
      return []
    }
  }
  const [товары, услуги] = await Promise.all([часть('Товары'), часть('Услуги')])
  const сырые = [
    ...товары.map((l) => ({ l, вид: 'товар' as const })),
    ...услуги.map((l) => ({ l, вид: 'услуга' as const })),
  ]

  // ── справочники одной волной: ключи собраны по всем строкам сразу.
  // За справочниками, которых ядро для этой базы не выпускает, не ходим вовсе.
  const ключи = (поле: string) => сырые.map(({ l }) => String(l[поле] ?? ''))
  const пусто = { карта: new Map<string, Record<string, unknown>>(), meta: null, сбой: false }
  const может = (сущ: string | null, ключиСписок: string[]) =>
    сущ ? справочник(запрос, сущ, ключиСписок) : Promise.resolve(пусто)
  const [ном, ед, страны, гтд, контр, дог] = await Promise.all([
    справочник(запрос, 'Catalog_Номенклатура', ключи('Номенклатура_Key')),
    может(сх.справочникЕдиниц, ключи(сх.единицаКлюч)),
    может(сх.справочникСтран, ключи('СтранаПроисхождения_Key')),
    может(сх.справочникГТД, ключи('НомерГТД_Key')),
    справочник(запрос, 'Catalog_Контрагенты',
      [String(док.Контрагент_Key ?? ''), String(док.Грузополучатель_Key ?? '')]),
    может(сх.справочникДоговоров, [String(док[сх.договорКлюч] ?? '')]),
  ])
  const сбои: Array<[string, boolean]> = [
    ['номенклатура (графы А и 1а)', ном.сбой], ['единицы измерения (графы 2 и 2а)', ед.сбой],
    ['страны происхождения (графы 10 и 10а)', страны.сбой], ['номера ГТД (графа 11)', гтд.сбой],
    ['контрагенты (графы 6, 6а, 6б)', контр.сбой], ['договоры (графа [8])', дог.сбой],
  ]
  for (const [имя, сбой] of сбои) if (сбой) несобрано.push(`справочник «${имя}» не ответил`)

  // ── строки бланка
  const строки: СтрокаДокумента1С[] = сырые.map(({ l, вид }) => {
    const н = ном.карта.get(String(l.Номенклатура_Key ?? ''))
    const е = ед.карта.get(String(l[сх.единицаКлюч] ?? ''))
    const с = страны.карта.get(String(l.СтранаПроисхождения_Key ?? ''))
    const г = гтд.карта.get(String(l.НомерГТД_Key ?? ''))
    return {
      вид,
      код: текст(н?.Code),
      // Содержание строки перекрывает название номенклатуры: в услугах пишут именно его.
      наименование: текст(l.Содержание) ?? текст(н?.Description),
      кодВида: текст(l.КодВидаТовара),
      // Единицу НЕ подставляем: см. defect №2 в шапке файла. Нет — значит прочерк.
      едКод: текст(е?.Code),
      едИмя: текст(е?.Description),
      количество: число1С(l.Количество),
      сумма: число1С(l.Сумма),
      ценаСырая: число1С(l.Цена),
      ндс: число1С(l.СуммаНДС),
      // В УТ ставка — ссылка на невыпускаемый справочник, поэтому её нет: графа 7 станет
      // прочерком. Выводить процент делением СуммаНДС на базу нельзя — это уже догадка.
      ставка: сх.ставкаПоле ? текст(l[сх.ставкаПоле]) : null,
      странаКод: текст(с?.Code),
      страна: текст(с?.Description),
      гтд: текст(г?.Code),
    }
  })

  // ── покупатель и грузополучатель
  const кпЗап = контр.карта.get(String(док.Контрагент_Key ?? ''))
  const покупатель: Контрагент1С | null = кпЗап
    ? {
      наименование: текст(кпЗап.НаименованиеПолное) ?? текст(кпЗап.Description),
      инн: текст(кпЗап.ИНН), кпп: текст(кпЗап.КПП), адрес: адресКонтрагента(кпЗап),
    }
    : null
  let грузополучатель: string | null = null
  const гпКлюч = String(док.Грузополучатель_Key ?? '')
  if (ссылкаЕсть(гпКлюч) && гпКлюч !== String(док.Контрагент_Key ?? '')) {
    const гп = контр.карта.get(гпКлюч)
    const имя = текст(гп?.НаименованиеПолное) ?? текст(гп?.Description)
    const адр = адресКонтрагента(гп)
    if (имя) грузополучатель = адр ? `${имя}, ${адр}` : имя
  }
  const адресДоставки = текст(док.АдресДоставки)
  if (адресДоставки) грузополучатель = грузополучатель ? `${грузополучатель}, ${адресДоставки}` : адресДоставки

  // ── основание передачи: текст документа, иначе договор из справочника
  const догЗап = дог.карта.get(String(док[сх.договорКлюч] ?? ''))
  const договор = текст(первое(док, сх.основание))
    ?? текст(догЗап?.НаименованиеПолное) ?? текст(догЗап?.Description) ?? null

  const свежесть = [шапка.meta, ном.meta, ед.meta, страны.meta, гтд.meta, контр.meta, дог.meta]
    .filter((m): m is OdataMeta => !!m)
    .reduce<OdataMeta | null>((худ, m) => {
      if (!худ) return m
      if ((m.stale && !худ.stale) || (m.stale === худ.stale && m.ageSec > худ.ageSec)) return m
      return худ
    }, null)

  const документ: Документ1С = {
    вид: 'реализация',
    номер: текст(док.Number),
    дата: текст(док.Date),
    // Bitrix-ловушка «0 == False» здесь не грозит: это булевы поля 1С, но проверяем по типу.
    проведён: typeof док.Posted === 'boolean' ? док.Posted : null,
    помеченНаУдаление: typeof док.DeletionMark === 'boolean' ? док.DeletionMark : null,
    // Ключевое место: отсутствие поля — это null, а НЕ true. См. defect №3 в шапке файла.
    // Имя поля разное: в БП «СуммаВключаетНДС», в УТ «ЦенаВключаетНДС».
    суммаВключаетНДС: (() => {
      const v = первое(док, сх.флагНалога)
      return typeof v === 'boolean' ? v : null
    })(),
    покупатель, грузополучатель, договор,
    // Валюту не подписываем рублём по одному факту наличия ссылки: справочник валют для
    // печати не запрашиваем, поэтому пишем то, что знаем — что валюта в документе указана.
    валюта: ссылкаЕсть(String(первое(док, ['ВалютаДокумента_Key', 'Валюта_Key']) ?? ''))
      ? 'Российский рубль, 643' : null,
    строки,
    суммаДокумента: число1С(док.СуммаДокумента),
    источник: база,
    ref,
  }
  return { документ, свежесть, пропуски: { несобрано } }
}
